import { AbstractApiService } from './abstract.api.service';
import { Injectable } from '@angular/core';
import { UserService } from '../user.service';
import { StorageService } from '../storage.service';
import { Subject } from 'rxjs';
import { ApiManagerService } from './api-manager.api.service';
import { User } from 'src/app/classes/User.class';
import {
  GET_LOGIN,
  GET_USERS,
  GET_APPROVED_USERS,
  GET_WAITING_REGISTRATION_USERS,
  GET_COMPANY_USERS,
  GET_FORGOT_PW_EMAIL_USERS,
  GET_USERS_BY_EMAIL,
} from '../../lib/contsants';

@Injectable({
  providedIn: 'root',
})
export class UserApiService extends AbstractApiService {
  getUserType = new Subject();

  constructor(
    storageService: StorageService,
    protected userService: UserService,
    protected apiManager: ApiManagerService,
  ) {
    super(storageService);
  }

  protected get adminData() {
    const token = this.storageService.returnJSONIfValid('USER_TOKEN');
    const userId = this.storageService.returnJSONIfValid('CURRENT_USER');
    return {
      token: token,
      userId: userId.id,
    };
  }

  protected getApi(endpoint: string): string {
    return this.apiManager.apiBuilder(endpoint);
  }

  protected setUser(data: { accessToken: string; user: User }) {
    this.storageService.storeJSONWithExpiry('USER_TOKEN', data.accessToken);
    this.storageService.storeJSONWithExpiry('CURRENT_USER', data.user);
    this.storageService.storeJSON(
      'SERVER',
      data.user['company.country'] !== 'CH' ? 'EU' : data.user['company.country'],
    );
  }

  protected setMowaAdminData(server: string, data: { accessToken: string; user: User }) {
    this.storageService.storeJSONWithExpiry('CURRENT_USER_' + server, data.user);
    this.storageService.storeJSONWithExpiry('USER_TOKEN_' + server, data.accessToken);
  }

  async login(userData: User) {
    const options: RequestInit = {
      body: JSON.stringify({
        strategy: 'local',
        email: userData.email,
        password: userData.password,
      }),
    };

    const data = await this.checkCorrectServer(options);

    if (data.user && data.user.role === 'mowa_admin') {
      this.getAllMowaAdminData(options);
    }

    if (!data.code) this.setUser(data);
    return data;
  }

  // due to the use of several servers, it is necessary to check on which server the user is located when logging in and return provided data if logging is successful.
  protected async checkCorrectServer(options) {
    const apisData = this.apiManager.getAllApi();
    let data;
    for (let i = 0; i < apisData.length; i++) {
      const response = await this.post(`${apisData[i].api + '/v1' + GET_LOGIN}`, options, true);
      data = response;
      if (response.user) break;
    }
    return data;
  }

  // provides mowa admin data from EU and CH Server
  protected getAllMowaAdminData(options) {
    const apisData = this.apiManager.getAllApi();
    apisData.forEach(async data => {
      const response = await this.post(`${data.api + '/v1' + GET_LOGIN}`, options, true);
      this.setMowaAdminData(data.server, response);
    });
  }

  async switchAPI(iso) {
    this.storageService.storeJSONWithExpiry(
      'CURRENT_USER',
      this.storageService.returnJSONIfValid('CURRENT_USER_' + iso),
    );
    this.storageService.storeJSONWithExpiry(
      'USER_TOKEN',
      this.storageService.returnJSONIfValid('USER_TOKEN_' + iso),
    );
    this.storageService.storeJSON('SERVER', iso);
  }

  async testQuizEndpoint() {
    const data = await this.get(this.getApi('/quizes'));
    return data;
  }

  async getAllUsers() {
    const data = await this.get(this.getApi(GET_USERS));
    return data;
  }

  async getAllApprovedUser() {
    const data = await this.get(this.getApi(GET_APPROVED_USERS));
    return data;
  }

  async getAdmin() {
    const data = await this.get(this.getApi(`${GET_USERS}/${this.adminData.userId}`));
    return data;
  }

  async getAdminRole() {
    const data = await this.getAdmin();
    return data.role;
  }

  async getIfMowaAdmin() {
    const data = await this.getAdmin();
    return data.role === 'mowa_admin';
  }

  async getUser(userId) {
    const data = await this.get(this.getApi(`${GET_USERS}/${userId}`));
    return data;
  }

  async getUsersForCompany(companyId) {
    const data = await this.get(this.getApi(`${GET_COMPANY_USERS}${companyId}`));
    return data.data;
  }

  async getRegistrationRequests() {
    const data = await this.get(this.getApi(GET_WAITING_REGISTRATION_USERS));
    return data;
  }

  async forgotPasswordEmail(userId) {
    const data = await this.requestForgotPasswordEmail(
      this.getApi(`${GET_FORGOT_PW_EMAIL_USERS}/${userId}`),
    );
    return data;
  }

  async checkUserRole(role) {
    switch (role) {
      case 'staff':
      case 'admin':
        this.userService.clearToken();
        return false;
      default:
        return true;
    }
  }

  async saveUser(userData) {
    const options: RequestInit = {
      body: JSON.stringify(userData),
    };
    const data =
      userData.id && userData !== null
        ? await this.patch(
            this.getApi(`${GET_USERS}${userData.id ? `/${userData.id}` : ``}`),
            options,
          )
        : await this.post(
            this.getApi(`${GET_USERS}${userData.id ? `/${userData.id}` : ``}`),
            options,
          );
    return data;
  }

  async updateUserRole(userId, role) {
    // get user and update role
    const user = await this.getUser(userId);
    user.role = role;
    const options: RequestInit = {
      body: JSON.stringify(user),
    };
    const data = await this.patch(this.getApi(`${GET_USERS}/${userId}`), options);
    return data;
  }

  async deleteUser(userId) {
    const data = await this.delete(this.getApi(`${GET_USERS}/${userId}`));
    return data;
  }

  async getUserByEmail(email: string) {
    const data = await this.get(this.getApi(`${GET_USERS_BY_EMAIL}${email}`));
    return data;
  }

  async checkIfUserExists(email: string): Promise<boolean> {
    const data = await this.getUserByEmail(email);
    return data.total > 0;
  }
}
